import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { SearchX } from 'lucide-react';
import { AudienceLevel, LEVEL_LABELS, MatchedModule } from '../../types/pathway';
import { SelectDropdown } from '../SelectDropdown';
import { ResetFiltersButton } from '../ResetFiltersButton';
import { ModuleCard } from './ModuleCard';
import { PathwayButton } from './PathwayButton';
interface ResultsPanelProps {
  terms: string[];
  results: MatchedModule[];
  pathwayIds: string[];
  audience: AudienceLevel | 'any';
  onToggleModule: (moduleId: string) => void;
}
const PAGE_SIZE = 8;
function Field({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <div data-id="element-1782" data-mp="mgm61vxoljst">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1783" data-mp="mg3xq0xqvf4j">
        {label}
      </span>
      {children}
    </div>;
}
export function ResultsPanel({
  terms,
  results,
  pathwayIds,
  audience,
  onToggleModule
}: ResultsPanelProps) {
  const [levelFilter, setLevelFilter] = useState<string>('any');
  const [categoryFilter, setCategoryFilter] = useState<string>('any');
  const [sort, setSort] = useState<string>('match');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const filtersActive = levelFilter !== 'any' || categoryFilter !== 'any' || sort !== 'match';
  const resetFilters = () => {
    setLevelFilter('any');
    setCategoryFilter('any');
    setSort('match');
    setPage(1);
  };

  // Topics are edited upstream and results re-rank in place, so a change must
  // not leave the rep stranded on a page that no longer exists.
  useEffect(() => {
    setPage(1);
  }, [terms]);
  const categories = useMemo(() => Array.from(new Set(results.map(r => r.module.category))).sort((a, b) => a.localeCompare(b)), [results]);
  const visible = useMemo(() => {
    let list = results.slice();
    if (levelFilter !== 'any') {
      list = list.filter(r => r.module.levels.includes(levelFilter as AudienceLevel));
    }
    if (categoryFilter !== 'any') {
      list = list.filter(r => r.module.category === categoryFilter);
    }
    if (sort === 'name') {
      list.sort((a, b) => a.module.name.localeCompare(b.module.name));
    } else if (sort === 'shortest') {
      list.sort((a, b) => a.module.durationHours - b.module.durationHours);
    } else {
      list.sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
    }
    return list;
  }, [results, levelFilter, categoryFilter, sort]);
  const pageCount = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageItems = visible.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const resetPage = <T,>(setter: (v: T) => void) => (value: T) => {
    setter(value);
    setPage(1);
  };
  return <section aria-label="Matching skill modules" data-id="element-1784" data-mp="1k0its4jhfnmg">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3" data-id="element-1785" data-mp="1k1bxewn59x0d">
        <div data-id="element-1786" data-mp="34jw1vziqp61f">
          <h2 className="text-xl font-bold text-gray-900" data-id="element-1787" data-mp="34kea7uiofapp">
            {visible.length} matching {visible.length === 1 ? 'module' : 'modules'}
          </h2>
          <p className="mt-1 text-sm text-gray-600" data-id="element-1788" data-mp="2vhmu3heuvqrq">
            {audience === 'any' ? 'Ranked by fit across all audiences.' : `Ranked for ${LEVEL_LABELS[audience].toLowerCase()}.`}
          </p>
        </div>
      </div>

      <div className="mb-5 rounded-xl border border-gray-100 bg-white p-5 shadow-sm" data-id="element-1789" data-mp="3o60u22pi8x5x">
        <div className="grid items-end gap-3 sm:grid-cols-[repeat(3,minmax(0,1fr))_auto]" data-id="element-1790" data-mp="3j923tv8ypad0">
          <Field label="Audience level" data-id="element-1791" data-mp="3efpoycu34hxg">
            <SelectDropdown ariaLabel="Filter by audience level" fullWidth scrollMenu value={levelFilter} onChange={resetPage(setLevelFilter)} options={[{
            value: 'any',
            label: 'All levels'
          }, ...(Object.keys(LEVEL_LABELS) as AudienceLevel[]).map(level => ({
            value: level,
            label: LEVEL_LABELS[level]
          }))]} data-id="element-1792" data-mp="3eeo3defg0d1h" />
          </Field>
          <Field label="Category" data-id="element-1793" data-mp="3cryuwctrz9wj">
            <SelectDropdown ariaLabel="Filter by category" fullWidth scrollMenu value={categoryFilter} onChange={resetPage(setCategoryFilter)} options={[{
            value: 'any',
            label: 'All categories'
          }, ...categories.map(category => ({
            value: category,
            label: category
          }))]} data-id="element-1794" data-mp="3hph6yc33a58r" />
          </Field>
          <Field label="Sort by" data-id="element-1795" data-mp="4gw2bf1f75ev">
            <SelectDropdown ariaLabel="Sort results" fullWidth value={sort} onChange={resetPage(setSort)} options={[{
            value: 'match',
            label: 'Best match'
          }, {
            value: 'shortest',
            label: 'Shortest first'
          }, {
            value: 'name',
            label: 'Module name'
          }]} data-id="element-1796" data-mp="s81cpeold5hs" />
          </Field>
          <ResetFiltersButton isActive={filtersActive} onReset={resetFilters} className="sm:mb-0.5" data-id="element-1797" data-mp="2qi8odcfo37ge" />
        </div>
      </div>

      {pageItems.length === 0 ? <div className="rounded-xl border border-gray-100 bg-white p-12 text-center shadow-sm" data-id="element-1798" data-mp="1yrugbwuc4pcn">
          <SearchX size={28} className="mx-auto text-gray-400" data-id="element-1799" data-mp="1tsriawcrvje6" />
          <h3 className="mt-3 text-lg font-semibold text-gray-900" data-id="element-1800" data-mp="27r93ambamhse">
            Nothing matches those filters
          </h3>
          <p className="mx-auto mt-1 max-w-sm text-sm text-gray-600" data-id="element-1801" data-mp="22s4rdnwzi5yd">
            Try widening the audience level or removing a search term.
          </p>
          <PathwayButton variant="secondary" size="sm" className="mt-4" onClick={resetFilters} data-id="element-1802" data-mp="3j8h8h9trk1xm">
            Clear Filters
          </PathwayButton>
        </div> : <motion.div layout className="space-y-3" data-id="element-1803" data-mp="2gsqcc50epd60">
          {pageItems.map(match => <motion.div key={match.module.id} layout initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-1804" data-mp="2kv3aa78ww6s8">
              <ModuleCard match={match} inPathway={pathwayIds.includes(match.module.id)} expanded={expandedId === match.module.id} onToggleExpand={() => setExpandedId(expandedId === match.module.id ? null : match.module.id)} onTogglePathway={() => onToggleModule(match.module.id)} data-id="element-1805" data-mp="1t5i8zsmjge6m" />
            </motion.div>)}
        </motion.div>}

      {pageCount > 1 && <nav aria-label="Results pages" className="mt-6 flex items-center justify-between" data-id="element-1806" data-mp="16vs0imove1nu">
          <PathwayButton variant="secondary" size="sm" onClick={() => setPage(Math.max(1, safePage - 1))} disabled={safePage === 1} data-id="element-1807" data-mp="11x6zil1sndyc">
            Previous
          </PathwayButton>
          <span className="text-sm text-gray-600" data-id="element-1808" data-mp="sxjuzqdrps12">
            Page {safePage} of {pageCount}
          </span>
          <PathwayButton variant="secondary" size="sm" onClick={() => setPage(Math.min(pageCount, safePage + 1))} disabled={safePage === pageCount} data-id="element-1809" data-mp="ny76z2b5ueww">
            Next
          </PathwayButton>
        </nav>}
    </section>;
}